import {
  HISTORY_BY_BOOK_TIME,
  HISTORY_BY_DEDUPE,
  HISTORY_BY_SCOPE,
  HISTORY_STORE,
  isConstraintError,
  localTransaction,
  requestToPromise,
  transactionDone,
} from '../localDb';
import { decodeEvent } from './payload';
import { HISTORY_PAGE_SIZE, type BookHistoryCursor, type BookHistoryEvent, type BookHistoryPage } from './types';

/**
 * IndexedDB port of drift_book_history_repository.dart. Events are stored
 * per account scope (`userScopeKey()`); history never leaves the device.
 */

export interface StoredHistoryEvent extends BookHistoryEvent {
  scope: string;
}

function toRecord(scope: string, e: BookHistoryEvent): StoredHistoryEvent {
  // Absent (not null) optional keys keep records out of the unique dedupe index.
  const record: StoredHistoryEvent = { scope, id: e.id, bookId: e.bookId, timestampMs: e.timestampMs, type: e.type, payload: e.payload };
  if (e.sessionId) record.sessionId = e.sessionId;
  if (e.source) record.source = e.source;
  if (e.dedupeKey) record.dedupeKey = e.dedupeKey;
  return record;
}

function fromRecord(raw: unknown): BookHistoryEvent | null {
  return decodeEvent(raw);
}

// ---------------------------------------------------------------- change feed

type WatchListener = () => void;
const watchers = new Map<string, Set<WatchListener>>();
const watchKey = (scope: string, bookId: string) => `${scope}\u0000${bookId}`;
const CHANNEL_NAME = 'plume-history';
let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (channel || typeof BroadcastChannel === 'undefined') return channel;
  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (e: MessageEvent<{ scope?: string; bookIds?: string[] }>) => {
    const { scope, bookIds } = e.data ?? {};
    if (typeof scope === 'string' && Array.isArray(bookIds)) notifyLocal(scope, bookIds);
  };
  return channel;
}

function notifyLocal(scope: string, bookIds: Iterable<string>) {
  for (const bookId of new Set(bookIds)) {
    watchers.get(watchKey(scope, bookId))?.forEach((l) => {
      try {
        l();
      } catch (err) {
        console.error('[history] watcher failed', err);
      }
    });
  }
}

function notifyChanged(scope: string, bookIds: string[]) {
  if (bookIds.length === 0) return;
  notifyLocal(scope, bookIds);
  try {
    getChannel()?.postMessage({ scope, bookIds: [...new Set(bookIds)] });
  } catch {
    /* channel closed */
  }
}

/** Calls `listener` whenever events for this book are appended (in this tab or another). */
export function watchBookHistory(scope: string, bookId: string, listener: WatchListener): () => void {
  getChannel();
  const key = watchKey(scope, bookId);
  let set = watchers.get(key);
  if (!set) {
    set = new Set();
    watchers.set(key, set);
  }
  set.add(listener);
  return () => {
    set!.delete(listener);
    if (set!.size === 0) watchers.delete(key);
  };
}

// ---------------------------------------------------------------- writes

/**
 * INSERT OR IGNORE: an event whose id, or (book, dedupeKey), already exists is
 * silently dropped. Resolves with the number of events actually stored.
 */
export async function appendHistoryEvents(scope: string, events: readonly BookHistoryEvent[]): Promise<number> {
  if (events.length === 0) return 0;
  const tx = await localTransaction(HISTORY_STORE, 'readwrite');
  const os = tx.objectStore(HISTORY_STORE);
  const dedupeIndex = os.index(HISTORY_BY_DEDUPE);
  const inserted: string[] = [];
  const batchKeys = new Set<string>();

  const add = (event: BookHistoryEvent) => {
    const req = os.add(toRecord(scope, event));
    req.onsuccess = () => inserted.push(event.bookId);
    req.onerror = (e) => {
      if (isConstraintError(req.error)) {
        // Keep the transaction alive: duplicates are expected.
        e.preventDefault();
        e.stopPropagation();
      }
    };
  };

  for (const event of events) {
    if (!event.dedupeKey) {
      add(event);
      continue;
    }
    // Checked explicitly (same transaction) rather than relying on the unique
    // index alone, so a rejected record can never linger after preventDefault.
    const batchKey = `${event.bookId}\u0000${event.dedupeKey}`;
    if (batchKeys.has(batchKey)) continue;
    batchKeys.add(batchKey);
    const check = dedupeIndex.getKey([scope, event.bookId, event.dedupeKey]);
    check.onsuccess = () => {
      if (check.result === undefined) add(event);
    };
  }
  await transactionDone(tx);
  notifyChanged(scope, inserted);
  return inserted.length;
}

export async function appendHistoryEvent(scope: string, event: BookHistoryEvent): Promise<boolean> {
  return (await appendHistoryEvents(scope, [event])) === 1;
}

// ---------------------------------------------------------------- reads

/**
 * Newest first. `cursor` continues after the last event of the previous page
 * (strictly older by (timestamp, id)).
 */
export async function fetchBookHistoryPage(
  scope: string,
  bookId: string,
  cursor: BookHistoryCursor | null = null,
  pageSize: number = HISTORY_PAGE_SIZE,
): Promise<BookHistoryPage> {
  const tx = await localTransaction(HISTORY_STORE, 'readonly');
  const index = tx.objectStore(HISTORY_STORE).index(HISTORY_BY_BOOK_TIME);
  const range = cursor
    ? IDBKeyRange.bound([scope, bookId], [scope, bookId, cursor.timestampMs, cursor.eventId], false, true)
    : IDBKeyRange.bound([scope, bookId], [scope, bookId, []]);
  const rows: BookHistoryEvent[] = [];
  await new Promise<void>((resolve, reject) => {
    const req = index.openCursor(range, 'prev');
    req.onsuccess = () => {
      const c = req.result;
      if (!c || rows.length > pageSize) return resolve();
      const event = fromRecord(c.value);
      if (event) rows.push(event);
      c.continue();
    };
    req.onerror = () => reject(req.error);
  });
  const hasMore = rows.length > pageSize;
  const events = hasMore ? rows.slice(0, pageSize) : rows;
  const last = events[events.length - 1];
  return { events, nextCursor: hasMore && last ? { timestampMs: last.timestampMs, eventId: last.id } : null };
}

/** Every stored event for a scope (or all scopes), oldest first — for backups. */
export async function exportHistoryEvents(scope?: string): Promise<StoredHistoryEvent[]> {
  const os = (await localTransaction(HISTORY_STORE, 'readonly')).objectStore(HISTORY_STORE);
  const rows = scope
    ? await requestToPromise(os.index(HISTORY_BY_SCOPE).getAll(IDBKeyRange.bound([scope], [scope, []])))
    : await requestToPromise(os.getAll());
  return (rows as unknown[])
    .map((raw) => {
      const event = fromRecord(raw);
      const s = (raw as { scope?: unknown }).scope;
      return event && typeof s === 'string' ? { ...event, scope: s } : null;
    })
    .filter((e): e is StoredHistoryEvent => e !== null)
    .sort((a, b) => a.timestampMs - b.timestampMs);
}

/** Deletes history for one scope, or everything. */
export async function clearHistory(scope?: string): Promise<void> {
  const tx = await localTransaction(HISTORY_STORE, 'readwrite');
  const os = tx.objectStore(HISTORY_STORE);
  if (!scope) {
    os.clear();
  } else {
    const req = os.index(HISTORY_BY_SCOPE).openKeyCursor(IDBKeyRange.bound([scope], [scope, []]));
    req.onsuccess = () => {
      const c = req.result;
      if (!c) return;
      os.delete(c.primaryKey);
      c.continue();
    };
  }
  await transactionDone(tx);
}
